// Teacher-facing wording for SubmissionBatchErrorCode. The raw server message
// names files and page indexes, which is right for a log and wrong for a wizard —
// but it is still a better fallback than nothing, so an unrecognised code falls
// through to it.
const MESSAGES = {
  EMPTY_STUDENTS: 'Assign at least one page to a student before continuing.',
  INVALID_ANSWER:
    'One of the responses could not be sent. Remove it and add it again.',
  DUPLICATE_PAGE: 'The same page is assigned to more than one student.',
  LIMIT_EXCEEDED: 'This upload is too large. Split it into smaller batches.',
  FILE_FORBIDDEN:
    'One of the uploaded files is no longer available to you. Upload it again.',
  ASSIGNMENT_NOT_FOUND: 'This task could not be found.',
  QUESTION_NOT_FOUND: 'This draft no longer has the question these pages answer.',
  FILE_NOT_FOUND: 'One of the uploaded files is missing. Upload it again.',
  STUDENT_NOT_IN_ASSIGNMENT:
    'A student is not enrolled in a class attached to this task.',
  PAGE_INDEX_OUT_OF_RANGE: 'A page was requested beyond the end of its file.',
  DRAFT_ALREADY_SUBMITTED:
    'An earlier upload on this task is still open. Close this window and open Upload responses again to continue it.',
  STUDENT_ALREADY_SUBMITTED:
    'A student already has a response for this task, so another cannot be uploaded for them. Remove them from this upload.',
};

/** Single home for this copy — the client raises some of these codes itself. */
export const submissionBatchCodeMessage = (code) =>
  MESSAGES[code] || 'Bulk upload failed.';

const studentName = (students, studentId) =>
  (students || []).find((student) => String(student.id) === String(studentId))
    ?.name || null;

const studentSuffix = (error, students) => {
  const name = studentName(students, error?.details?.studentId);
  return name ? `(${name})` : '';
};

// Round 4c (06 §5.8): the server's copy for these codes names the student and says
// what to do, so it is shown as sent (the student named after it only if it doesn't).
const SERVER_WORDED = new Set(['RESUBMISSION_NO_LONGER_ROUTABLE']);
const NO_LONGER_ROUTABLE_FALLBACK =
  "A student has started redoing their response, so the pages routed to them can't be moved. Put their pages back as they were to save the other changes.";

const serverWorded = (error, students) => {
  const message = error?.message || NO_LONGER_ROUTABLE_FALLBACK;
  const name = studentName(students, error?.details?.studentId);
  return name && !message.includes(name) ? `${message} (${name})` : message;
};

export const submissionBatchErrorMessage = (error, students = []) =>
  (SERVER_WORDED.has(error?.code) ||
    (error?.code === 'DRAFT_ALREADY_SUBMITTED' && error?.details?.pendingRedoSubmissionIds?.length))
    ? serverWorded(error, students)
    : [
      MESSAGES[error?.code] || error?.message || 'Bulk upload failed.',
      studentSuffix(error, students),
    ]
      .filter(Boolean)
      .join(' ');
